'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { MarketCoin, DerivativesSnapshot } from '@glintfans/shared';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import { formatPercent, formatVolume } from '@/lib/format';
import { useFxStore, fmtFx } from '@/lib/fx';
import { useRealtimeTickers, mergeLive } from '@/lib/realtime';
import { useWatchlistMembership } from '@/lib/useWatchlistMembership';
import CoinLogo from './CoinLogo';
import { TableSkeleton } from './Skeleton';
import EmptyState from './EmptyState';

type Tab =
  | 'all'
  | 'trending'
  | 'new'
  | 'gainers'
  | 'losers'
  | 'volume'
  | 'volatility'
  | 'oi'
  | 'funding';

const TAB_API: Record<Tab, string> = {
  all: '/market/markets?tab=all&sort=mcap&order=desc',
  trending: '/market/markets?tab=trending',
  new: '/market/markets?tab=new',
  gainers: '/market/markets?sort=change&order=desc',
  losers: '/market/markets?sort=change&order=asc',
  volume: '/market/markets?sort=volume&order=desc',
  volatility: '/market/markets?sort=volatility&order=desc',
  oi: '/market/derivatives/rankings?sort=oi&limit=50',
  funding: '/market/derivatives/rankings?sort=funding&limit=50',
};

function tabLabel(t: ReturnType<typeof translate>, key: Tab): string {
  switch (key) {
    case 'all': return t.markets.tabAll;
    case 'trending': return t.markets.tabTrending;
    case 'new': return t.markets.tabNew;
    case 'gainers': return t.rank.rankGainers;
    case 'losers': return t.rank.rankLosers;
    case 'volume': return t.rank.rankVolume;
    case 'volatility': return t.rank.rankVolatility;
    case 'oi': return t.rank.rankOi;
    case 'funding': return t.rank.rankFunding;
    default: return '';
  }
}

/** D14: 当前排序字段（显示在标题区，固定可见） */
function sortLabel(zh: boolean, key: Tab, isSpot: boolean): string {
  switch (key) {
    case 'trending': return zh ? '热度' : 'Trending';
    case 'new': return zh ? '上市时间' : 'Listed';
    case 'gainers': return zh ? '涨幅' : '24h %';
    case 'losers': return zh ? '跌幅' : '24h %';
    case 'volume': return zh ? '成交额' : 'Turnover';
    case 'volatility': return zh ? '波动率' : 'Volatility';
    case 'oi': return zh ? '持仓量' : 'Open Interest';
    case 'funding': return zh ? '资金费率' : 'Funding';
    default: return isSpot ? (zh ? '成交额' : 'Turnover') : (zh ? '市值' : 'Market Cap');
  }
}

/** AUD04: derive base/quote from a derivatives symbol without corrupting
 *  identity — ETHUSDC stays ETH/USDC, BTCUSDT stays BTC/USDT. */
const derivBase = (sym: string): string => sym.replace(/USDT$/, '').replace(/USDC$/, '').replace(/USD$/, '');
const derivQuote = (sym: string): string => (sym.endsWith('USDC') ? 'USDC' : sym.endsWith('USD') ? 'USD' : 'USDT');


import { downloadCsv } from '@/lib/csv';

export default function MarketTable({ refreshTick = 0 }: { refreshTick?: number }) {
  const router = useRouter();
  const { language, user } = useAppStore();
  const t = translate(language);
  const live = useRealtimeTickers();
  const [tab, setTab] = useState<Tab>('all');
  const [type, setType] = useState<'futures' | 'spot'>('futures');
  const [search, setSearch] = useState('');

  // v0.8: 下拉刷新 —— 页面层 tick 变化时强制 refetch（保留 tab/search state）
  const prevTick = useRef(refreshTick);
  useEffect(() => {
    if (refreshTick && refreshTick !== prevTick.current) {
      prevTick.current = refreshTick;
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);
  // AUD21: shared watchlist membership — the star mirrors the real groups,
  // so it no longer starts empty on a logged-in session.
  const { isStarred, toggle: toggleWatch, loading: wlLoading } = useWatchlistMembership();

  const isDeriv = tab === 'oi' || tab === 'funding';
  const isSpot = type === 'spot';

  // Switch out of deriv tabs when toggling to spot (deriv rankings are futures-only)
  const switchType = (nt: 'futures' | 'spot') => {
    if (nt === 'spot' && (tab === 'oi' || tab === 'funding')) setTab('all');
    setType(nt);
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['markets', tab, search, type],
    queryFn: () =>
      api.get<any>(
        `${TAB_API[tab]}${isSpot && !isDeriv ? '&type=spot' : ''}&search=${encodeURIComponent(search)}&quote=USDT&pageSize=100`,
      ),
    refetchInterval: 10_000,
  });

  const raw = data ?? [];
  const items: Array<MarketCoin | DerivativesSnapshot> = Array.isArray(raw)
    ? raw
    : (raw as { items?: Array<MarketCoin | DerivativesSnapshot> })?.items ?? [];

  const rows = useMemo(() => {
    if (isDeriv) return items as DerivativesSnapshot[];
    return (items as MarketCoin[]).map((c) => mergeLive(c, live[c.symbol]));
  }, [items, live, isDeriv]);

  const tabs: Tab[] = isSpot
    ? ['all', 'trending', 'new', 'gainers', 'losers', 'volume', 'volatility']
    : ['all', 'trending', 'new', 'gainers', 'losers', 'volume', 'volatility', 'oi', 'funding'];

  const exportCsv = () => {
    if (rows.length === 0) return;
  const headers = language !== 'en'
    ? ['排名', '名称', '价格', '24H涨跌', '24H最高', '24H最低', '24H成交额', '资金费率', '持仓量']
    : ['Rank', 'Symbol', 'Price', '24H Change', '24H High', '24H Low', '24H Volume', 'Funding Rate', 'Open Interest'];
  const data = rows.map((it, idx) => {
    const r = it as unknown as Record<string, unknown>;
    return [
      idx + 1,
      String(r.symbol ?? ''),
      String(r.price ?? ''),
      String(r.priceChangePercent24h ?? ''),
      String(r.high ?? ''),
      String(r.low ?? ''),
      String(r.quoteVolume ?? ''),
      r.fundingRate != null ? (Number(r.fundingRate) * 100).toFixed(4) + '%' : '',
      String(r.openInterestUsd ?? ''),
    ];
  });
  downloadCsv(`ailumix-markets-${new Date().toISOString().slice(0, 10)}.csv`, headers, data);
};

// FX conversion (settings currency)
  const { currency, rates } = useFxStore();
  useEffect(() => {
    useFxStore.getState().load();
  }, []);
  const fxRate = rates[currency] ?? 1;
  const fxPrice = (usd: number) => fmtFx(usd, fxRate, currency);

  return (
    <div className="card mt-4 !p-0 overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-gf-border px-4 pt-3 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-center gap-1.5 pb-2">
          <button
            onClick={() => switchType('futures')}
            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors sm:text-xs ${
              !isSpot
                ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
            }`}
          >
            {language !== 'en' ? '永续' : 'Perp'}
          </button>
          <button
            onClick={() => switchType('spot')}
            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors sm:text-xs ${
              isSpot
                ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
            }`}
          >
            {language !== 'en' ? '现货' : 'Spot'}
          </button>
          <span className="mx-1 hidden h-4 w-px bg-gf-border sm:block" />
        </div>
        <div className="flex gap-1 overflow-x-auto whitespace-nowrap pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-1.5">
          {tabs.map((tb) => (
            <button
              key={tb}
              onClick={() => setTab(tb)}
              className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] transition-colors sm:px-3 sm:text-xs ${
                tab === tb
                  ? 'border-gf-accent bg-gf-accent/15 font-medium text-gf-accent'
                  : 'border-gf-border bg-gf-surface2 text-gf-muted hover:border-gf-accent/40 hover:text-gf-text'
              }`}
            >
              {tabLabel(t, tb)}
            </button>
          ))}
        </div>
        <div className="pb-2 sm:ml-auto">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.markets.searchPlaceholder}
            className="input w-full !py-1.5 text-xs sm:!w-56"
          />
        </div>
      </div>

      <div className="mb-2 flex items-center justify-end">
        <button
          type="button"
          onClick={exportCsv}
          className="rounded-full border border-gf-border bg-gf-surface2 px-2.5 py-1 text-[11px] text-gf-muted transition-colors hover:border-gf-accent/40 hover:text-gf-text sm:px-3 sm:text-xs"
        >
          {language !== 'en' ? '导出 CSV' : 'Export CSV'}
        </button>
      </div>
      {/* D14: 当前排序字段固定显示（标题区，双端可见）+ 榜类型说明 */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-gf-border/50 px-4 py-1.5 text-[10px] text-gf-muted">
        <span className="flex items-center gap-1">
          <span className="text-gf-muted">{language !== 'en' ? '排序' : 'Sort'}:</span>
          <span className="rounded bg-gf-surface2 px-1.5 py-px font-medium text-gf-text">{sortLabel(language !== 'en', tab, isSpot)}</span>
        </span>
        <span className="mx-1 hidden h-3 w-px bg-gf-border sm:block" />
        <span className="hidden sm:inline">
          {isDeriv
            ? (language !== 'en' ? '市场级合约（分交易所）' : 'Market-level contracts (per exchange)')
            : tab === 'all'
              ? (language !== 'en' ? '资产级市值（按基础资产去重）' : 'Asset-level cap (deduped by base asset)')
              : (language !== 'en' ? '市场级排行（分交易所）' : 'Market-level (per exchange)')}
        </span>
      </div>

      {isLoading && <TableSkeleton />}
      {isError && (
        <EmptyState
          error
          title={t.common.error}
          description={language !== 'en' ? '行情加载失败，请重试' : 'Failed to load markets. Please retry.'}
          actionLabel={t.common.retry}
          onAction={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <>
          {/* Mobile: D13 三行卡片 —— 身份 / 价格涨跌+排序字段带标签 / 快捷研究入口 */}
          <div className="flex flex-col gap-2 p-2 sm:hidden">
            {rows.map((it, idx) => {
              const symbol = (it as MarketCoin).symbol ?? (it as DerivativesSnapshot).symbol;
              const added = isStarred(symbol);
              const rankField = (label: string, node: ReactNode, extra?: ReactNode) => (
                <div className="flex min-w-0 items-center justify-between gap-1 rounded-md border border-gf-border/60 bg-gf-surface2/50 px-1 py-1.5">
                  <span className="shrink-0 whitespace-nowrap text-[10px] text-gf-muted">{label}</span>
                  <span className="min-w-0 truncate">{node}</span>
                </div>
              );
              return (
                <div key={symbol} className="rounded-md border border-gf-border bg-gf-surface p-3">
                  {/* 行1：身份 */}
                  <div className="flex items-center gap-2">
                    <button
                      title={user ? (added ? t.markets.added : t.markets.addWatch) : t.markets.loginRequired}
                      onClick={() => {
                        if (!user) { router.push('/login'); return; }
                        if (added) return;
                        toggleWatch.mutate(symbol);
                      }}
                      className={`w-5 shrink-0 text-sm leading-none ${added ? 'text-gf-warn' : 'text-gf-muted'}`}
                    >
                      {added ? t.markets.added : t.markets.addWatch}
                    </button>
                    <span className="w-4 shrink-0 text-[10px] text-gf-muted">{idx + 1}</span>
                    <Link href={`/coin/${symbol}${isSpot && !isDeriv ? '?type=spot' : ''}`} className="flex min-w-0 flex-1 items-center gap-2">
                      <CoinLogo symbol={symbol} size={24} />
                      <div className="min-w-0 leading-tight">
                        <div className="truncate text-sm font-medium">
                          {isDeriv ? `${derivBase(symbol)}/${derivQuote(symbol)}` : `${(it as MarketCoin).baseAsset}/${(it as MarketCoin).quoteAsset}`}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-gf-muted">
                          <span className={`rounded px-1 py-px ${isSpot && !isDeriv ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                            {isSpot && !isDeriv
                              ? (language !== 'en' ? '现货' : 'Spot')
                              : (language !== 'en' ? '永续' : 'Perp')}
                          </span>
                          {!isDeriv && <span className="truncate">{(it as MarketCoin).exchange || 'Binance'}</span>}
                          {(it as MarketCoin).isNewListing && (
                            <span className="rounded bg-gf-warn/20 px-1 text-[9px] font-semibold text-gf-warn">{t.common.new}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                    {/* 行1 右侧：价格 + 涨跌幅（与币种同行） */}
                    <div className="shrink-0 text-right">
                      <div className="truncate font-mono text-sm">
                        {isDeriv ? formatPercent((it as DerivativesSnapshot).fundingRate * 100) : fxPrice((it as MarketCoin).price)}
                      </div>
                      <div className={`font-mono text-xs ${isDeriv ? ((it as DerivativesSnapshot).fundingRate >= 0 ? 'positive' : 'negative') : ((it as MarketCoin).priceChangePercent24h >= 0 ? 'positive' : 'negative')}`}>
                        {isDeriv ? (language !== 'en' ? '资金费率' : 'Funding') : formatPercent((it as MarketCoin).priceChangePercent24h)}
                      </div>
                    </div>
                  </div>

                  {/* 行2：指标卡片 + 研究入口（同一行） */}
                  <div className="mt-2 flex items-center gap-1">
                    <div className="grid min-w-0 flex-1 grid-cols-2 gap-1">
                      {isDeriv ? (
                        <>
                          {rankField('OI', <span className="whitespace-nowrap font-mono text-xs">{formatVolume((it as DerivativesSnapshot).openInterestUsd)}</span>)}
                          {rankField(language !== 'en' ? '多空比' : 'L/S', <span className="whitespace-nowrap font-mono text-xs">{(it as DerivativesSnapshot).longShortRatio.toFixed(2)}</span>)}
                        </>
                      ) : (
                        <>
                          {tab === 'all' && rankField(language !== 'en' ? '市值' : 'Mcap', <span className="font-mono text-xs" title={((it as any).marketCapUsd || (it as any).mcap) ? `$${Number(((it as any).marketCapUsd || (it as any).mcap) ?? 0).toLocaleString('en-US')}` : undefined}>{formatVolume(((it as any).marketCapUsd || (it as any).mcap) ?? 0) || '—'}</span>)}
                          {rankField(language !== 'en' ? '24H 成交额' : '24H Turnover', <span className="font-mono text-xs">{formatVolume((it as MarketCoin).quoteVolume24h)}</span>)}
                          {tab === 'volatility' && rankField(language !== 'en' ? '波动' : 'Volat', <span className="font-mono text-xs">{(it as any).volatility != null ? formatPercent((it as any).volatility) : '—'}</span>)}
                        </>
                      )}
                    </div>
                    <Link
                      href={`/coin/${symbol}${isSpot && !isDeriv ? '?type=spot' : ''}`}
                      onClick={(e) => {
                        if (!user) { e.preventDefault(); router.push('/login'); }
                      }}
                      className="shrink-0 rounded-full border border-gf-accent/40 bg-gf-accent/10 px-1.5 py-1 text-[10px] text-gf-accent"
                    >
                      {language !== 'en' ? '研究 →' : 'Research →'}
                    </Link>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && (
              <EmptyState
                compact
                title={t.markets.noResults}
                description={language !== 'en' ? '试试切换榜单或清空搜索' : 'Try another ranking or clear the search.'}
              />
            )}
          </div>

          {/* Desktop: full table */}
          <div className="hidden overflow-x-auto sm:block">
          <table className="table-base">
            <thead className="bg-gf-surface2/60">
              <tr>
                <th className="w-8"></th>
                <th className="w-10 text-left text-gf-muted">#</th>
                <th className="text-left">{t.markets.colSymbol}</th>
                {tab === 'oi' && (
                  <>
                    <th className="text-right">{t.rank.rankColOi}</th>
                    <th className="text-right">{t.rank.rankColRatio}</th>
                    <th className="text-right">{t.rank.rankColFunding}</th>
                  </>
                )}
                {tab === 'funding' && (
                  <>
                    <th className="text-right">{t.rank.rankColFunding}</th>
                    <th className="text-right">{t.rank.rankColOi}</th>
                    <th className="text-right">{t.rank.rankColRatio}</th>
                  </>
                )}
                {!isDeriv && (
                  <>
                    <th className="text-right">{t.markets.colPrice}</th>
                    <th className="text-right">{t.markets.colChange}</th>
                    <th className="hidden text-right md:table-cell">{t.markets.colHigh}</th>
                    <th className="hidden text-right md:table-cell">{t.markets.colLow}</th>
                    <th className="text-right">{t.markets.colVolume}</th>
                  </>
                )}
                {tab === 'all' && (
                  <th className="text-right">
                    {language !== 'en' ? '总市值' : 'Market Cap'}
                  </th>
                )}
                <th className="text-right">
                  {language !== 'en' ? '研究' : 'Research'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((it, idx) => {
                const symbol = (it as MarketCoin).symbol ?? (it as DerivativesSnapshot).symbol;
                const added = isStarred(symbol);
                if (isDeriv) {
                  const d = it as DerivativesSnapshot;
                  const fundCell = (
                    <td className={`text-right font-mono ${d.fundingRate >= 0 ? 'positive' : 'negative'}`}>
                      {formatPercent(d.fundingRate * 100)}
                    </td>
                  );
                  const oiCell = (
                    <td className="text-right font-mono">{formatVolume(d.openInterestUsd)}</td>
                  );
                  const lsCell = (
                    <td className="text-right font-mono">{d.longShortRatio.toFixed(2)}</td>
                  );
                  return (
                    <tr key={symbol} className="hover:bg-gf-surface2/50">
                      <td>
                        <button
                          title={user ? (added ? t.markets.added : t.markets.addWatch) : t.markets.loginRequired}
                          onClick={() => {
                            if (!user) { router.push('/login'); return; }
                            if (added) return;
                            toggleWatch.mutate(symbol);
                          }}
                          className={`text-base leading-none transition-colors ${added ? 'text-gf-warn' : 'text-gf-muted hover:text-gf-warn'}`}
                        >
                          {added ? t.markets.added : t.markets.addWatch}
                        </button>
                      </td>
                      <td className="text-xs text-gf-muted">{idx + 1}</td>
                      <td>
                        <Link href={`/coin/${symbol}`} className="flex items-center gap-2 whitespace-nowrap font-medium hover:text-gf-accent">
                          <CoinLogo symbol={symbol} size={24} />
                          <span className="text-gf-text">{derivBase(symbol)}</span>
                          <span className="text-xs text-gf-muted">/ {derivQuote(symbol)}</span>
                        </Link>
                      </td>
                      {tab === 'oi' && <>{oiCell}{lsCell}{fundCell}</>}
                      {tab === 'funding' && <>{fundCell}{oiCell}{lsCell}</>}
                      <td className="text-right">
                        <Link
                          href={`/coin/${symbol}`}
                          onClick={(e) => {
                            if (!user) { e.preventDefault(); router.push('/login'); }
                          }}
                          className="rounded-full border border-gf-accent/40 bg-gf-accent/10 px-2 py-1 text-[10px] text-gf-accent"
                        >
                          {language !== 'en' ? '研究' : 'Research'}
                        </Link>
                      </td>
                    </tr>
                  );
                }
                const c = it as MarketCoin;
                const pct = c.priceChangePercent24h;
                const mcap = (c as any).marketCapUsd || (c as any).mcap || null;
                return (
                  <tr key={c.symbol} className="hover:bg-gf-surface2/50">
                    <td>
                      <button
                        title={user ? (added ? t.markets.added : t.markets.addWatch) : t.markets.loginRequired}
                        onClick={() => {
                          if (!user) { router.push('/login'); return; }
                          if (added) return;
                          toggleWatch.mutate(c.symbol);
                        }}
                        className={`text-base leading-none transition-colors ${added ? 'text-gf-warn' : 'text-gf-muted hover:text-gf-warn'}`}
                      >
                        {added ? t.markets.added : t.markets.addWatch}
                      </button>
                    </td>
                    <td className="text-xs text-gf-muted">{idx + 1}</td>
                    <td>
                      <Link href={`/coin/${c.symbol}${isSpot ? '?type=spot' : ''}`} className="flex items-center gap-2 whitespace-nowrap font-medium hover:text-gf-accent">
                        <CoinLogo symbol={c.symbol} size={28} />
                        <div className="leading-tight">
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-gf-text">{c.baseAsset}</span>
                            <span className="text-xs text-gf-muted">/ {c.quoteAsset}</span>
                            {c.isNewListing && (
                              <span className="rounded bg-gf-warn/20 px-1 text-[10px] font-semibold text-gf-warn">{t.common.new}</span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gf-muted">
                            <span className={`rounded px-1 py-px ${isSpot ? 'bg-gf-surface2 text-gf-muted' : 'bg-gf-accent/10 text-gf-accent'}`}>
                              {isSpot
                                ? (language !== 'en' ? '现货' : 'Spot')
                                : (language !== 'en' ? '永续合约' : 'Perp')}
                            </span>
                            <span>{c.exchange || 'Binance'}</span>
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="whitespace-nowrap text-right font-mono">{fxPrice(c.price)}</td>
                    <td className={`whitespace-nowrap text-right font-mono ${pct >= 0 ? 'positive' : 'negative'}`}>{formatPercent(pct)}</td>
                    <td className="hidden whitespace-nowrap text-right font-mono text-gf-muted md:table-cell">{fxPrice(c.high24h)}</td>
                    <td className="hidden whitespace-nowrap text-right font-mono text-gf-muted md:table-cell">{fxPrice(c.low24h)}</td>
                    <td className="whitespace-nowrap text-right font-mono">{formatVolume(c.quoteVolume24h)}</td>
                    {tab === 'all' && (
                      <td className="whitespace-nowrap text-right font-mono text-xs text-gf-muted">
                        {mcap ? (
                          <span title={`$${mcap.toLocaleString('en-US')}`}>{formatVolume(mcap)}</span>
                        ) : '—'}
                      </td>
                    )}
                    <td className="text-right">
                      <Link
                        href={`/coin/${c.symbol}${isSpot ? '?type=spot' : ''}`}
                        onClick={(e) => {
                          if (!user) { e.preventDefault(); router.push('/login'); }
                        }}
                        className="rounded-full border border-gf-accent/40 bg-gf-accent/10 px-2 py-1 text-[10px] text-gf-accent"
                      >
                        {language !== 'en' ? '研究' : 'Research'}
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center">
                    <EmptyState
                      compact
                      title={t.markets.noResults}
                      description={language !== 'en' ? '试试切换榜单或清空搜索' : 'Try another ranking or clear the search.'}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
